/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2023
 * @flow
 */

import React from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from "@ag-grid-community/react";
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { Box, Paper, Typography } from '@material-ui/core';
import useStyles from './useStyles';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
]);

const Table = ({ columnDefs, rowData, title, displayFooter }) => {
  const gridStyle = React.useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [gridAPI, setGridAPI] = React.useState(null);
  const [totals, setTotals] = React.useState({});

  const classes = useStyles();

  const onGridReady = React.useCallback((params) => {
    setGridAPI(params.api);
    const rows = params?.api?.rowModel?.rowsToDisplay || [];
    const columns = params?.api?.columnController?.columnDefs || [];
    calculateTotal(rows, columns);
  }, []);

  React.useEffect(() => {
    if (gridAPI) {
      const rows = gridAPI?.rowModel?.rowsToDisplay || [];
      const columns = gridAPI?.columnController?.columnDefs || [];
      calculateTotal(rows, columns);
    }
  }, [rowData]);

  const calculateTotal = (rows, columns) => {
    if (displayFooter) {
      let totals = {};
      columns.forEach(col => {
        if (col?.footer) {
          const sum = rows.reduce((total, eachRow) => {
            return total + (eachRow?.data?.[col.field] || 0);
          }, 0);
          totals = {
            ...totals,
            [col.field]: sum,
          };
        }
      });
      setTotals(totals);
    }
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <Typography className={classes.title} color="textSecondary">{title}</Typography>
      <div style={{ height: displayFooter ? 249 : 270 }}>
        <div style={gridStyle} className="ag-theme-material">
          <AgGridReact
            key='key-ag-grid-community'
            rowData={rowData}
            defaultColDef={{
              filterParams: {
                newRowsAction: 'keep'
              }
            }}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            rowHeight={40}
            onFilterChanged={(ev) => {
              const rows = ev?.api?.rowModel?.rowsToDisplay;
              const columns = ev?.api?.columnController?.columnDefs;
              calculateTotal(rows, columns);
            }}
          />
        </div>
      </div>
      {displayFooter && (
        <Box display={'flex'} alignItems={'center'}>
          {columnDefs.map(col =>
            <Box key={`key-${col.field}-${col.headerName}`} flex={col.flex || 1} display={'flex'} justifyContent={'center'}>
              {col.footer && (
                <Typography className={classes.totalLabel} color="textSecondary">{`Total: ${totals?.[col.field] || 0}`}</Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default Table;

Table.propTypes = {
  columnDefs: PropTypes.array,
  rowData: PropTypes.array,
  title: PropTypes.string,
  displayFooter: PropTypes.bool,
};

Table.defaultProps = {
  columnDefs: [],
  rowData: [],
  title: '',
  displayFooter: false,
};
